import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, IconName, Tooltip as Tip } from '@unmind/design-system';

import { useCoachingAndTherapyUserSessionLimits } from 'Book/shared/hooks';
import { Wrapper } from './styles';

type TooltipKey =
  | 'tooltip.remaining'
  | 'tooltip.noneRemaining'
  | 'tooltip.expired';

const Tooltip = ({
  textKey,
  values,
}: {
  textKey: TooltipKey;
  values: Record<string, string | number>;
}) => {
  const { t } = useTranslation('book', {
    keyPrefix: 'therapyAndCoaching.sessionAllowance',
  });

  return (
    <Tip
      triggerElement={<Icon iconName={IconName.InformationCircleSimple} />}
      tooltipContents={t(textKey, values)}
      placement="bottom"
      accessibilityId="allowance-tooltip"
    />
  );
};

export const SessionAllowance = () => {
  const { i18n, t } = useTranslation('book', {
    keyPrefix: 'therapyAndCoaching.sessionAllowance',
  });
  const {
    sessionLimit,
    remainingSessions,
    expiryDate,
    sessionsExpired,
    loading,
    error,
  } = useCoachingAndTherapyUserSessionLimits();

  const hasNoAllowanceData =
    sessionLimit === undefined || remainingSessions === undefined;

  if (loading || error || hasNoAllowanceData) return null;

  const formattedExpiryDate = expiryDate
    ? new Intl.DateTimeFormat(i18n.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(expiryDate)
    : null;

  if (sessionsExpired) {
    return (
      <Wrapper data-testid="session-allowance">
        {t('pill.expired')}
        {formattedExpiryDate && (
          <Tooltip
            textKey="tooltip.expired"
            values={{ date: formattedExpiryDate }}
          />
        )}
      </Wrapper>
    );
  }

  if (remainingSessions === 0) {
    return (
      <Wrapper data-testid="session-allowance">
        {t('pill.noneRemaining')}
        <Tooltip
          textKey="tooltip.noneRemaining"
          values={{ total: sessionLimit }}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper data-testid="session-allowance">
      {t('pill.remaining', {
        remaining: remainingSessions,
        total: sessionLimit,
      })}
      {formattedExpiryDate && (
        <Tooltip
          textKey="tooltip.remaining"
          values={{ remaining: remainingSessions, date: formattedExpiryDate }}
        />
      )}
      <small>{t('limit')}</small>
    </Wrapper>
  );
};
